import axios from "axios";
import { DEFAULT_OPERATOR } from "../../utils/operators";

/**
 * API Configuration — MangoTV
 * Pattern: mirrors glotv apiConfig.js
 *
 * Two API layers:
 *   1. CRM API (https://tvanywhereonline.com/cm/api) — OTP, subscriber mgmt, config
 *   2. Client API (dynamic, fetched from CRM landing endpoint) — login, content, streaming
 *
 * Landing endpoint: GET {CRM_API}/client/?operator_uid=9mobile&mode=web
 * Response: { status: "ok", data: { landing_page: { api_url: "...", ... } } }
 */

// ===== CRM API (fixed) =====
const CRM_API_BASE_URL = "https://tvanywhereonline.com/cm/api";

// ===== Client API (dynamic, fetched from CRM landing) =====
const FALLBACK_CLIENT_API_URL = "https://ott.tvanywhereafrica.com:28182";
let CLIENT_API_BASE_URL = null;

// Cache settings
const CACHE_KEY = "mango_client_api_url";
const CACHE_TIMESTAMP_KEY = "mango_client_api_url_ts";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Detect environment — CRA sets NODE_ENV at build time
const IS_PRODUCTION = process.env.NODE_ENV === "production";

// In-memory cache for full landing_page data (avoids duplicate CRM calls)
let cachedLandingPage = null;
// Deduplication: if a fetch is already in-flight, reuse its promise
let landingPageFetchPromise = null;

// CRM API auth headers (required for all CRM requests)
const CRM_AUTH_HEADERS = {
  Password: "tva12345#",
  Username: "tva",
};

// ===== GETTERS =====

export const getCrmApiUrl = () => CRM_API_BASE_URL;

// Proxied CRM path — uses Vercel rewrites in production, setupProxy.js in dev
// Both environments route /cm/api/* to the CRM backend
// Note: For the landing endpoint specifically, use fetchAndCacheLandingPage() instead
// as Vercel rewrites don't set the Host header (changeOrigin) which the CRM requires
export const getProxiedCrmApiUrl = () => "/cm/api";

export const getClientApiUrl = () => {
  // In production, use the dynamic api_url from CRM landing page (e.g. https://ott.tvanywhereafrica.com:28182)
  // This ensures login and content calls go to the correct server, not a hardcoded rewrite.
  // Falls back to FALLBACK_CLIENT_API_URL if the dynamic URL hasn't been fetched yet.
  if (IS_PRODUCTION) {
    return CLIENT_API_BASE_URL || FALLBACK_CLIENT_API_URL;
  }
  // In dev, return empty string so requests use setupProxy.js (relative paths like /api/client/...)
  return "";
};

export const getCrmAuthHeaders = () => CRM_AUTH_HEADERS;

/** Get cached landing_page data (populated by initializeApiConfig) */
export const getCachedLandingPage = () => cachedLandingPage;

/**
 * Fetch the CRM landing page and cache the full response.
 * Used by both initializeApiConfig (for api_url) and fetchCrmProducts (for product_categories).
 * Avoids duplicate calls to the same CRM endpoint.
 *
 * In development: uses setupProxy.js (/cm/api/client/...) which sets changeOrigin: true
 * In production:  calls CRM API directly (same pattern as OTP — works on any host)
 *
 * @param {string} operatorUid
 * @returns {Promise<Object|null>} — landing_page object or null
 */
export const fetchAndCacheLandingPage = async (operatorUid = DEFAULT_OPERATOR.operator_uid) => {
  // Return cached data if available
  if (cachedLandingPage) return cachedLandingPage;

  // Deduplication: if a fetch is already in-flight, wait for it
  if (landingPageFetchPromise) return landingPageFetchPromise;

  const doFetch = async () => {
    try {
      let url;
      let config;

      if (IS_PRODUCTION) {
        // Production: call CRM API directly (same as OTP calls — host-agnostic, works on Vercel, Netlify, etc.)
        url = `${CRM_API_BASE_URL}/client/?operator_uid=${operatorUid}&mode=web`;
        config = { headers: CRM_AUTH_HEADERS, timeout: 15000 };
      } else {
        // Dev: route through setupProxy.js which sets changeOrigin: true
        // Only send CRM auth headers (Password + Username)
        // Do NOT add Content-Type or Cache-Control — CRM returns landing_page: null with those extra headers
        url = `/cm/api/client/?operator_uid=${operatorUid}&mode=web`;
        config = { headers: CRM_AUTH_HEADERS, timeout: 10000 };
      }

      const response = await axios.get(url, config);
      const responseData = response.data;

      if (responseData.status === "ok" && responseData.data) {
        const d = responseData.data;

        // Primary: landing_page object (contains api_url + product_categories)
        if (d.landing_page && typeof d.landing_page === "object" && Object.keys(d.landing_page).length > 0) {
          cachedLandingPage = d.landing_page;
          return cachedLandingPage;
        }

        // Fallback: CRM sometimes returns landing_page: null but includes
        // package_product alongside other config keys. Build a synthetic
        // landing_page so the rest of the app works.
        if (d.package_product) {
          cachedLandingPage = {
            product_categories: Array.isArray(d.package_product) ? d.package_product : [d.package_product],
          };
          return cachedLandingPage;
        }
      }

      console.warn("fetchAndCacheLandingPage: no usable data in response", IS_PRODUCTION ? "(production)" : "(dev)");
      return null;
    } catch (error) {
      console.error("fetchAndCacheLandingPage error:", error.message, error.response?.status, error.response?.data);
      return null;
    } finally {
      landingPageFetchPromise = null;
    }
  };

  landingPageFetchPromise = doFetch();
  return landingPageFetchPromise;
};

/**
 * Universal CRM API request helper.
 * Routes through serverless function in production (correct Host header),
 * through setupProxy in development (changeOrigin: true).
 *
 * @param {string} method — "GET" or "POST"
 * @param {string} endpoint — CRM path after /cm/api/ (e.g. "purchase/", "orders/")
 * @param {Object} params — query parameters (e.g. { operator_uid: "9mobile" })
 * @param {Object} [body] — POST body (for POST requests)
 * @param {number} [timeout=15000] — request timeout in ms
 * @returns {Promise<Object>} — CRM response data
 */
export const crmRequest = async (method, endpoint, params = {}, body = null, timeout = 15000) => {
  const queryParams = new URLSearchParams(params).toString();
  let url;
  let headers;

  if (IS_PRODUCTION) {
    // Production: call CRM API directly (same as OTP — works on any host)
    url = `${CRM_API_BASE_URL}/${endpoint}${queryParams ? `?${queryParams}` : ""}`;
    headers = { ...CRM_AUTH_HEADERS, "Content-Type": "application/json" };
  } else {
    // Development: route through setupProxy.js
    url = `/cm/api/${endpoint}${queryParams ? `?${queryParams}` : ""}`;
    headers = { ...CRM_AUTH_HEADERS, "Content-Type": "application/json" };
  }

  if (method === "POST") {
    const response = await axios.post(url, body, { headers, timeout });
    return response.data;
  }
  const response = await axios.get(url, { headers, timeout });
  return response.data;
};

// ===== CACHE HELPERS =====

const getCachedApiUrl = () => {
  try {
    const cachedUrl = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!cachedUrl || !timestamp) return null;
    if (Date.now() - parseInt(timestamp, 10) > CACHE_DURATION) {
      clearCache();
      return null;
    }
    return cachedUrl;
  } catch {
    return null;
  }
};

const setClientApiUrl = (url) => {
  CLIENT_API_BASE_URL = url;
  try {
    localStorage.setItem(CACHE_KEY, url);
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error("Failed to cache API URL:", error);
  }
};

const clearCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  } catch {
    // silent
  }
};

// ===== INITIALIZATION =====

/**
 * Initialize API config by calling the CRM landing endpoint.
 * Fetches the dynamic Client API URL for the operator.
 *
 * CRM Endpoint: GET {CRM_API}/client/?operator_uid={operatorUid}&mode=web
 * Response: { status: "ok", data: { landing_page: { api_url: "https://...", ... } } }
 *
 * @param {string} operatorUid — e.g. "9mobile"
 * @returns {Promise<string>} — the Client API base URL
 */
export const initializeApiConfig = async (operatorUid = DEFAULT_OPERATOR.operator_uid) => {
  // Check cache first — avoid unnecessary API calls
  const cached = getCachedApiUrl();
  if (cached) {
    CLIENT_API_BASE_URL = cached;
    // Still fetch landing page in background if not cached (for product_categories etc.)
    if (!cachedLandingPage) {
      fetchAndCacheLandingPage(operatorUid);
    }
    return cached;
  }

  try {
    // Use shared fetch-and-cache to get landing_page (one call serves all consumers)
    const landingPage = await fetchAndCacheLandingPage(operatorUid);

    if (landingPage?.api_url) {
      let apiUrl = landingPage.api_url;
      // API sometimes returns api_url without protocol (e.g. "ott.tvanywhereafrica.com:28182")
      // Prepend https:// if missing to ensure valid URL
      if (apiUrl && !apiUrl.startsWith("http://") && !apiUrl.startsWith("https://")) {
        apiUrl = `https://${apiUrl}`;
      }

      setClientApiUrl(apiUrl);
      return apiUrl;
    } else {
      console.warn("Landing API: unexpected response, using fallback");
      setClientApiUrl(FALLBACK_CLIENT_API_URL);
      return FALLBACK_CLIENT_API_URL;
    }
  } catch (error) {
    console.error("Landing API error:", error.message);
    setClientApiUrl(FALLBACK_CLIENT_API_URL);
    return FALLBACK_CLIENT_API_URL;
  }
};

/**
 * Force-refresh the API configuration (clears cache)
 */
export const refreshApiConfig = async (operatorUid) => {
  clearCache();
  CLIENT_API_BASE_URL = null;
  cachedLandingPage = null;
  landingPageFetchPromise = null;
  return initializeApiConfig(operatorUid);
};

/**
 * Fetch complete CRM config for the operator (landing_page data).
 * Used to get dashboard categories, banners, etc.
 *
 * @param {string} operatorUid
 * @returns {Promise<Object|null>} — { landing_page: { ... } } or null
 */
export const fetchCrmConfig = async (operatorUid = DEFAULT_OPERATOR.operator_uid) => {
  try {
    // Use cached landing page if available, otherwise fetch
    const landingPage = await fetchAndCacheLandingPage(operatorUid);
    if (landingPage) {
      return { landing_page: landingPage };
    }
    return null;
  } catch (error) {
    console.error("CRM config fetch error:", error.message);
    return null;
  }
};

// ===== AXIOS INSTANCE (for Client API requests) =====

export const apiClient = axios.create({
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor — attach auth token + dynamic base URL
apiClient.interceptors.request.use(
  (config) => {
    // Ensure base URL is always current
    if (!config.baseURL) {
      config.baseURL = getClientApiUrl();
    }
    const token = localStorage.getItem("mango_access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401 (token expired) with retry
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh once per request (_retry flag)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Dynamic import to avoid circular deps
        const { refresh_token } = await import("../constants/refreshToken");
        const newToken = await refresh_token();

        // Update the failed request with new token and retry
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch {
        // Refresh failed — redirect to login
        localStorage.removeItem("mango_access_token");
        localStorage.removeItem("mango_refresh_token");
        localStorage.removeItem("mango_authenticated");
        window.location.href = "/";
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// ===== GLOBAL AXIOS INTERCEPTOR =====
// Catches 401s on plain axios calls (used by account.js, etc.)
// Only intercepts Client API calls (not CRM calls which use different auth)

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || "";

    // Only refresh for Client API calls — NOT CRM calls
    // CRM URLs like /cm/api/client/ contain "/api/client/" but use different auth
    const isCrmCall = url.includes("/cm/api/") || url.includes("/api/crm-proxy");
    const isClientApiCall =
      !isCrmCall &&
      (url.includes("/api/client/") ||
       url.includes("ott.tvanywhereafrica.com"));

    if (
      error.response?.status === 401 &&
      isClientApiCall &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const { refresh_token } = await import("../constants/refreshToken");
        const newToken = await refresh_token();

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch {
        localStorage.removeItem("mango_access_token");
        localStorage.removeItem("mango_refresh_token");
        localStorage.removeItem("mango_authenticated");
        window.location.href = "/";
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);
